import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useTable, useRowSelect, usePagination } from "react-table";
import { COLUMNS } from "./columns";
import { Checkbox } from "../../../../../components/common/Checkbox";
import { noDataPresent } from "../../../../../locales/en";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import DualPagination from "../../../../common/DualPagination";
import SortableHeaderCell from "../../../../common/SortableHeaderCell";
import {
  PAGE_SIZE_50,
  PAGE_SIZE_OPTIONS,
  ZERO_NUMBER,
  NUMBER_ONE,
  SKELETON_ROWS_10
} from "../../../../../utils/constants/appConstants";
import "./../../reportable/reportable.scss";

const SelectionHeader = ({ getToggleAllRowsSelectedProps }) => (
  <Checkbox {...getToggleAllRowsSelectedProps()} className="headercheckBox" />
);

SelectionHeader.propTypes = {
  getToggleAllRowsSelectedProps: PropTypes.func.isRequired
};

const SelectionCellWrapper = ({ row }) => (
  <Checkbox {...row.getToggleRowSelectedProps()} className="bodycheckBox" />
);

SelectionCellWrapper.propTypes = {
  row: PropTypes.object.isRequired
};

const TotalOwingTable = ({
  data,
  loading,
  totalPages = ZERO_NUMBER,
  pageIndex = ZERO_NUMBER,
  pageSize = PAGE_SIZE_50,
  setPageIndex,
  setPageSize,
  columnOrders = {},
  handleSort = () => {}
}) => {
  const baseColumns = useMemo(() => COLUMNS, []);
  const sortableColumns = useMemo(
    () => baseColumns.map((column) => column.accessor),
    [baseColumns]
  );

  const SelectionCell = useCallback(
    ({ row }) => <SelectionCellWrapper row={row} />,
    []
  );

  const tableColumns = useMemo(() => {
    return [
      {
        id: "selection",
        Header: SelectionHeader,
        Cell: SelectionCell
      },
      ...baseColumns
    ];
  }, [baseColumns, SelectionCell]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns: tableColumns,
        data: data || [],
        initialState: { pageIndex, pageSize },
        manualPagination: true,
        autoResetPage: false,
        autoResetSelectedRows: false
      },
      usePagination,
      useRowSelect
    );

  const canPreviousPage = pageIndex > ZERO_NUMBER;
  const canNextPage =
    pageIndex < totalPages - NUMBER_ONE && data && data.length > ZERO_NUMBER;
  const pageCount = totalPages;
  const pageOptions = useMemo(
    () => [...Array(totalPages).keys()],
    [totalPages]
  );

  const gotoPage = (updater) => {
    setPageIndex(updater);
  };

  const previousPage = () => {
    const newPage = Math.max(ZERO_NUMBER, pageIndex - NUMBER_ONE);
    setPageIndex(newPage);
  };

  const nextPage = () => {
    const newPage =
      pageIndex < totalPages - NUMBER_ONE ? pageIndex + NUMBER_ONE : pageIndex;
    setPageIndex(newPage);
  };

  return (
    <>
      <table
        className="mt-2 mb-3 reportable1 bgless_table w-100"
        {...getTableProps()}
      >
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key, ...rest } = headerGroup.getHeaderGroupProps();
            return (
              <tr className="parentrow" {...rest} key={key}>
                {headerGroup.headers.map((column) => {
                  const isSortable = sortableColumns.includes(column.id);
                  return (
                    <SortableHeaderCell
                      key={column.id}
                      column={column}
                      isSortable={isSortable}
                      columnOrders={columnOrders}
                      handleSort={handleSort}
                    />
                  );
                })}
              </tr>
            );
          })}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading && (
            <TableSkeleton
              rows={SKELETON_ROWS_10}
              columns={baseColumns.length + NUMBER_ONE}
            />
          )}
          {!loading && page.length === ZERO_NUMBER && (
            <tr>
              <td
                colSpan={baseColumns.length + NUMBER_ONE}
                className="text-center"
              >
                {noDataPresent}
              </td>
            </tr>
          )}
          {!loading &&
            page.length > ZERO_NUMBER &&
            page.map((row) => {
              prepareRow(row);
              const isSelected = row.isSelected;

              return (
                <tr
                  className={`parentrow ${isSelected ? "selected" : ""}`}
                  {...row.getRowProps()}
                  key={row.id}
                >
                  {row.cells.map((cell) => (
                    <td key={cell.column.id} {...cell.getCellProps()}>
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
        </tbody>
      </table>

      {totalPages > ZERO_NUMBER && (
        <DualPagination
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={pageCount}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data.length}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      )}
    </>
  );
};

TotalOwingTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  totalPages: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  setPageIndex: PropTypes.func,
  setPageSize: PropTypes.func,
  columnOrders: PropTypes.object,
  handleSort: PropTypes.func
};

export default TotalOwingTable;
